import { useState } from 'react';
import { useNotify, useRecordContext, useRefresh, useCanAccess } from 'ra-core';
import {
  regenererLeSecretDappel,
  revoquerLeSecretDappel,
} from '@groupe-trousseau/shared/api-client';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Confirm } from '@/components/admin/confirm';
import { deballer, lireLErreur } from '../../fournisseurs/client';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LE SECRET D'APPEL — F-57, ET LA VALEUR NE PARAÎT QU'UNE FOIS.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * F-57 : « la structure porte son **EMPREINTE, jamais sa valeur** (Q537). Le réaffichage est
 * donc impossible **par construction**. » §5 le redit en refus, et c'est la ligne que
 * `CDC-5` désigne comme la plus importante de son tableau de cloisonnement : « le compte qui
 * voit tout lit la valeur du secret **une seule fois, à l'émission, et ne la relit jamais**.
 * Même patron que l'empreinte de mot de passe. »
 *
 * **Il n'y a donc aucun bouton « voir le secret » sur cet écran**, et ce n'est pas un oubli :
 * la matrice porte `secret.show` à `PERSONNE`, et le contrat ne rend qu'un booléen dérivé.
 * §6.14, champs interdits en réponse : « **l'empreinte, toujours et sans exception** ».
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **DEUX GESTES, ET LEURS CONSÉQUENCES SONT ÉCRITES SUR L'ÉCRAN.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * **Régénérer**, c'est « **écraser l'empreinte, sans période de recouvrement** — l'ancienne
 * valeur est invalide immédiatement ». Cas de bord 5 : « le secret est régénéré pendant que
 * la structure appelle → l'appel en cours **échoue**, sans période de recouvrement, **aucune
 * notification** ».
 *
 * **Révoquer**, c'est « **vider le champ** ». Sa portée est sa structure seule : « **une clé
 * partagée n'existe pas dans ce produit** : la révocation d'une structure ne coupe aucune
 * autre », et « **il n'anonymise aucun compte** ».
 *
 * **Aucun email ne part**, ni à l'émission, ni à la régénération, ni à la révocation : « **la
 * structure le découvre par l'erreur de son prochain appel.** » C'est pourquoi les deux
 * confirmations le disent — sans quoi la personne qui administre croirait que quelqu'un est
 * prévenu.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUE CET ÉCRAN NE PROPOSE PAS, ET LE MOTIF EST ÉCRIT.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * **Aucune rotation périodique**, Q554, et trois raisons : « le produit n'a qu'un seul régime
 * de rotation, écrit trois fois ; une rotation périodique ferait de la coupure d'appels **un
 * incident programmé à date fixe** sur la route qui crée les comptes et les cours ; et
 * **aucune tâche planifiée ne pourrait la déclencher**, la liste étant fermée à deux
 * passages. »
 *
 * **Aucune date de dernier changement non plus** : le coût est écrit en F-57 pour qu'un ajout
 * le contredise visiblement — « un secret déposé chez un tiers vaut **indéfiniment** tant que
 * personne ne le retire, et **le produit ne porte aucune date qui rappellerait de le
 * faire** ». Le contrat n'en rend aucune : l'inventer ici serait la première copie.
 */
export function SecretDappel() {
  const structure = useRecordContext<{ id: string; secretEmis?: boolean }>();
  const notifier = useNotify();
  const rafraichir = useRefresh();
  const { canAccess: peutRegenerer } = useCanAccess({
    resource: 'secret',
    action: 'regenerer-le-secret',
  });
  const { canAccess: peutRevoquer } = useCanAccess({
    resource: 'secret',
    action: 'revoquer-le-secret',
  });

  const [confirmation, setConfirmation] = useState<'regeneration' | 'revocation' | null>(
    null,
  );
  /**
   * **La valeur émise, tenue en état local le temps de l'afficher.**
   *
   * Elle ne va **ni dans un magasin, ni dans le cache de `ra-core`, ni dans l'adresse**. Un
   * secret qui entrerait dans un cache serait relisible après un retour en arrière, ce qui
   * annulerait exactement la garantie que l'empreinte achète. Recharger la page la perd, et
   * c'est le comportement voulu : F-57, « affiché une seule fois et jamais réaffiché ».
   */
  const [valeurEmise, setValeurEmise] = useState<string | null>(null);

  if (structure === undefined) return null;
  if (!peutRegenerer && !peutRevoquer) return null;

  const regenerer = async () => {
    try {
      const reponse = await deballer(
        regenererLeSecretDappel({ path: { id: structure.id } }),
      );
      setValeurEmise(reponse.secret);
      rafraichir();
    } catch (erreur) {
      notifier(lireLErreur(erreur).message, { type: 'error' });
    } finally {
      setConfirmation(null);
    }
  };

  const revoquer = async () => {
    try {
      await deballer(revoquerLeSecretDappel({ path: { id: structure.id } }));
      setValeurEmise(null);
      notifier('Le secret d’appel est révoqué. Les appels de la structure échouent désormais.', {
        type: 'info',
      });
      rafraichir();
    } catch (erreur) {
      notifier(lireLErreur(erreur).message, { type: 'error' });
    } finally {
      setConfirmation(null);
    }
  };

  return (
    <section className="flex flex-col gap-3">
      <h3 className="text-sm font-medium">Secret d’appel</h3>
      <p className="text-muted-foreground text-sm">
        {structure.secretEmis === true
          ? 'Un secret est émis. Sa valeur n’est pas lisible : la structure n’en porte que l’empreinte.'
          : 'Aucun secret n’est émis. Les appels de provisionnement de cette structure échouent.'}
      </p>

      {valeurEmise !== null && (
        <Alert>
          <AlertTitle>
            Voici le secret. Il ne sera plus jamais affiché — copiez-le maintenant.
          </AlertTitle>
          <AlertDescription>
            <code className="mt-1 block break-all font-mono text-xs">{valeurEmise}</code>
            <span className="mt-2 block">
              Transmettez-le hors du produit. Aucun email ne part, et le perdre oblige à
              régénérer — ce qui coupe immédiatement les appels en cours de la structure.
            </span>
          </AlertDescription>
        </Alert>
      )}

      <div className="flex gap-2">
        {peutRegenerer && (
          <Button variant="outline" onClick={() => setConfirmation('regeneration')}>
            Régénérer le secret
          </Button>
        )}
        {peutRevoquer && structure.secretEmis === true && (
          <Button variant="destructive" onClick={() => setConfirmation('revocation')}>
            Révoquer le secret
          </Button>
        )}
      </div>

      <Confirm
        isOpen={confirmation === 'regeneration'}
        title="Régénérer le secret d’appel ?"
        content={
          'L’ancienne valeur devient invalide IMMÉDIATEMENT, sans période de recouvrement. ' +
          'Un appel en cours de la structure échouera. Aucun email ne part : la structure le ' +
          'découvrira par l’erreur de son prochain appel. La nouvelle valeur s’affiche une ' +
          'seule fois.'
        }
        onConfirm={() => void regenerer()}
        onClose={() => setConfirmation(null)}
      />
      <Confirm
        isOpen={confirmation === 'revocation'}
        title="Révoquer le secret d’appel ?"
        content={
          'Le champ est vidé : la structure ne peut plus appeler l’API. Aucun compte n’est ' +
          'anonymisé, aucune autre structure n’est touchée, et aucun email ne part. C’est le ' +
          'dernier geste de l’extinction d’une structure (F-68).'
        }
        onConfirm={() => void revoquer()}
        onClose={() => setConfirmation(null)}
      />
    </section>
  );
}
